import { useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Bug, CircleAlert, MonitorSmartphone, PowerOff } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ROUTES } from '@/constants';
import { useDashboard } from '@/services/queries';
import { AccentCard, AccentStat, type AccentTone } from '@/shared/ui/accent-card';
import { SectionHeading } from '@/shared/ui/section-heading';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { ErrorState } from '@/components/common';
import { cn } from '@/lib/utils';
import type { TelemetrySummary } from '@/types';
import { Sparkline, type SparkTone } from './Sparkline';

/**
 * The opening screen of ops.nudent.ai, in the design's cards.
 *
 * Composed from two modules that know nothing about each other - telemetry and
 * distribution - which is exactly why the server composes it at the API layer
 * rather than inside either one.
 *
 * ── The card is the design's, and so is the accent bar ──────────────────────
 *
 * `AccentCard` carries the transcribed shape - 14px radius, a 32px icon tile, a
 * hairline under the title, and a 5px bar flush to the bottom edge. The bar is
 * what makes five of these read as one instrument panel rather than five
 * separate boxes.
 *
 * The tone is computed from the value, not fixed per metric: three crashes is
 * red and zero crashes is green, and a card that is permanently red because
 * crashes are a bad thing in general tells nobody anything.
 *
 * ── Every counter goes somewhere ────────────────────────────────────────────
 *
 * A number with no way through to the rows behind it tells somebody there is a
 * problem and then makes them go looking for it. The three that have a list
 * behind them link to it; the two that do not are plain.
 */

/** Windows the server will accept. It caps at a fortnight (336 hours). */
const WINDOWS = [
  { hours: 24, label: '24h' },
  { hours: 72, label: '3d' },
  { hours: 168, label: '7d' },
  { hours: 336, label: '14d' },
] as const;

interface Metric {
  key: keyof TelemetrySummary['series'];
  label: string;
  value: number;
  icon: LucideIcon;
  to?: string;
  /** Non-zero is a problem worth showing in red rather than as a neutral count. */
  alarming?: boolean;
  /** What the number means when it is zero - "all reporting", say. */
  note?: string;
  /** The design's semantic colour for this card's sparkline. */
  spark: SparkTone;
}

export function FleetSummary() {
  const [windowHours, setWindowHours] = useState<number>(24);
  const query = useDashboard(windowHours);

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load the dashboard"
        description={query.error.message}
        action={
          <Button
            variant="outline"
            onClick={() => {
              void query.refetch();
            }}
          >
            Try again
          </Button>
        }
      />
    );
  }

  const telemetry = query.data?.telemetry;
  const windowLabel =
    WINDOWS.find((option) => option.hours === windowHours)?.label ?? `${String(windowHours)}h`;

  const metrics: Metric[] = [
    {
      key: 'activeDevices',
      spark: 'positive',
      label: 'Active machines',
      value: telemetry?.activeDevices ?? 0,
      icon: MonitorSmartphone,
      note: `reporting in the last ${windowLabel}`,
    },
    {
      key: 'inactiveDevices',
      spark: 'caution',
      label: 'Not reporting',
      value: telemetry?.inactiveDevices ?? 0,
      icon: PowerOff,
      // A machine that has stopped sending heartbeats is either off or broken,
      // and this console cannot tell which. Worth surfacing either way.
      alarming: (telemetry?.inactiveDevices ?? 0) > 0,
      note: 'switched off, or broken',
    },
    {
      key: 'crashes',
      spark: 'negative',
      label: 'Crashes',
      value: telemetry?.crashes ?? 0,
      icon: Bug,
      to: ROUTES.crashes,
      alarming: (telemetry?.crashes ?? 0) > 0,
    },
    {
      key: 'errorLogs',
      spark: 'negative',
      label: 'Error logs',
      value: telemetry?.errorLogs ?? 0,
      icon: CircleAlert,
      to: ROUTES.logs,
      alarming: (telemetry?.errorLogs ?? 0) > 0,
    },
    {
      key: 'openPanelIssues',
      spark: 'caution',
      label: 'Open issues',
      value: telemetry?.openPanelIssues ?? 0,
      icon: AlertTriangle,
      to: ROUTES.issues,
      note: 'raised against the portals',
    },
  ];

  return (
    <section>
      <SectionHeading
        action={
          <div className="flex gap-1" role="group" aria-label="Time window">
            {WINDOWS.map((option) => (
              <button
                key={option.hours}
                type="button"
                onClick={() => {
                  setWindowHours(option.hours);
                }}
                aria-pressed={windowHours === option.hours}
                className={cn(
                  'h-8 rounded-lg px-3 text-xs font-semibold transition-colors',
                  windowHours === option.hours
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      >
        Health signals
      </SectionHeading>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          const bad = Boolean(metric.alarming) && metric.value > 0;
          const tone: AccentTone = bad ? 'negative' : 'brand';

          const card = (
            <AccentCard
              icon={<Icon size={16} strokeWidth={2} />}
              tone={tone}
              bar={tone}
              title={metric.label}
              className={cn('h-full', metric.to && 'transition-colors hover:border-primary/40')}
            >
              {query.isPending ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <>
                  <AccentStat
                    value={<span className="tabular-nums">{metric.value.toLocaleString()}</span>}
                    delta={
                      bad
                        ? `needs attention`
                        : (metric.note ?? (metric.alarming ? 'nothing outstanding' : undefined))
                    }
                    deltaTone={bad ? 'negative' : 'positive'}
                  />
                  {/* Same window as the number, in nine slices, oldest first. */}
                  <Sparkline
                    values={telemetry?.series[metric.key] ?? []}
                    tone={metric.spark}
                    className="mt-3"
                  />
                </>
              )}
            </AccentCard>
          );

          return metric.to ? (
            <Link
              key={metric.key}
              to={metric.to}
              className="rounded-[14px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {card}
            </Link>
          ) : (
            <div key={metric.key}>{card}</div>
          );
        })}
      </div>

      <p className="mt-3 text-[11.5px] text-muted-foreground">
        Counts cover the last {windowLabel}.
        {query.isFetching && !query.isPending ? ' Refreshing…' : ''}
      </p>
    </section>
  );
}
